import React, { useMemo, useState, useEffect } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import PermissionSegmented from './PermissionSegmented';
import { UI_MODULE_GROUPS, PERMISSION_META } from './config';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EditAccessSheet = ({
  open,
  onOpenChange,
  userData,          // {user, staff_role_type, assigned_athletes, effective_permissions, has_overrides, ...}
  moduleLabels,
  staffRoles,
  defaultPermissions,
  allAthletes,
  onSaved,
}) => {
  const [detail, setDetail] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [staffRole, setStaffRole] = useState('');
  const [useRoleDefault, setUseRoleDefault] = useState(true);
  const [athleteIds, setAthleteIds] = useState([]);
  const [athleteSearch, setAthleteSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const role = userData?.user?.role;
  const isStaff = role === 'Staff';

  // Load full detail when opened
  useEffect(() => {
    if (!open || !userData) return;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/permissions/user/${userData.user.id}`);
        setDetail(res.data);
        setPermissions({ ...res.data.effective_permissions });
        setStaffRole(res.data.staff_role_type || '');
        setAthleteIds(res.data.assigned_athletes || []);
        setUseRoleDefault(!res.data.permission_overrides || Object.keys(res.data.permission_overrides || {}).length === 0);
        setAthleteSearch('');
      } catch (_e) {
        toast.error('Failed to load user permissions');
        onOpenChange(false);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, userData, onOpenChange]);

  const applyRoleDefault = (roleType) => {
    if (!roleType || !defaultPermissions[roleType]) return;
    const next = {};
    Object.keys(moduleLabels).forEach((m) => { next[m] = defaultPermissions[roleType][m] || 'none'; });
    setPermissions(next);
  };

  const handleRoleChange = (roleType) => {
    setStaffRole(roleType);
    if (useRoleDefault) applyRoleDefault(roleType);
  };

  const handleToggleUseDefault = (checked) => {
    setUseRoleDefault(checked);
    if (checked) {
      if (isStaff && staffRole) applyRoleDefault(staffRole);
      else if (detail?.role_defaults) setPermissions({ ...detail.role_defaults });
    }
  };

  const handlePermissionChange = (module, level) => {
    setPermissions((prev) => ({ ...prev, [module]: level }));
  };

  const filteredAthletes = useMemo(() => {
    if (!athleteSearch) return allAthletes;
    const q = athleteSearch.toLowerCase();
    return allAthletes.filter((a) => (a.full_name || '').toLowerCase().includes(q));
  }, [allAthletes, athleteSearch]);

  const toggleAthlete = (id) => {
    setAthleteIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const selectAllAthletes = () => setAthleteIds(filteredAthletes.map((a) => a.id));
  const clearAllAthletes = () => setAthleteIds([]);
  const matchRoleDefaultAthletes = () => {
    // Role-default for athletes is "empty" until admin assigns. Match by clearing.
    setAthleteIds([]);
    toast.info('Athlete assignment matched to role default (empty).');
  };

  const handleSave = async () => {
    if (!detail) return;
    setSaving(true);
    try {
      const payload = {
        user_id: detail.user.id,
        staff_role_type: isStaff ? (staffRole || null) : null,
        permissions,
        use_role_defaults: useRoleDefault,
      };
      if (isStaff) payload.assigned_athletes = athleteIds;

      await axios.put(`${API}/permissions/user/${detail.user.id}`, payload);
      toast.success('Access updated. Active sessions invalidated.');
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save access');
    } finally {
      setSaving(false);
    }
  };

  const handleResetDefault = async () => {
    if (!detail) return;
    if (!window.confirm('Reset this user to their role default? Current custom permissions will be cleared.')) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/permissions/user/${detail.user.id}/overrides`);
      toast.success('Reverted to role default.');
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reset');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl md:max-w-2xl overflow-y-auto"
        data-testid="edit-access-sheet"
      >
        <SheetHeader className="pb-4 border-b">
          <SheetTitle>Edit Access</SheetTitle>
          <SheetDescription>
            Configure module permissions and athlete visibility for this user.
          </SheetDescription>
        </SheetHeader>

        {loading || !detail ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
          </div>
        ) : (
          <div className="space-y-6 py-4">
            {/* Summary */}
            <div className="flex items-start gap-4 flex-wrap">
              <div className="w-12 h-12 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-lg font-bold">
                {detail.user.full_name?.charAt(0) || '?'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-base font-semibold truncate">{detail.user.full_name}</h3>
                  <Badge variant="outline">{detail.user.role}</Badge>
                  {detail.staff_role_type && (
                    <Badge className="bg-purple-100 text-purple-800">{detail.staff_role_type}</Badge>
                  )}
                </div>
                <p className="text-sm text-gray-500 truncate">{detail.user.email}</p>
              </div>
            </div>

            {/* Staff role selector */}
            {isStaff && (
              <div>
                <Label>Staff Role</Label>
                <Select value={staffRole} onValueChange={handleRoleChange}>
                  <SelectTrigger data-testid="edit-role-select">
                    <SelectValue placeholder="Select role type..." />
                  </SelectTrigger>
                  <SelectContent>
                    {staffRoles.map((r) => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Use Role Default toggle */}
            <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-900 p-3 rounded-lg">
              <div>
                <Label className="text-sm font-medium">Use Role Default</Label>
                <p className="text-xs text-gray-500 mt-0.5">
                  {useRoleDefault
                    ? 'Permissions are locked to the role default'
                    : 'Custom overrides enabled — edit freely'}
                </p>
              </div>
              <Switch
                checked={useRoleDefault}
                onCheckedChange={handleToggleUseDefault}
                data-testid="use-role-default-toggle"
              />
            </div>

            {/* Permission grid */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-sm font-medium">Permissions</Label>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span title={PERMISSION_META.view.tooltip}><strong>View</strong> = read-only</span>
                  <span>·</span>
                  <span title={PERMISSION_META.edit.tooltip}><strong>Edit</strong> = modify</span>
                  <span>·</span>
                  <span title={PERMISSION_META.delete.tooltip}><strong>Delete</strong> = full control</span>
                </div>
              </div>

              <div className="space-y-4">
                {UI_MODULE_GROUPS.map((group) => (
                  <div key={group.key}>
                    <div className="flex items-baseline justify-between mb-2">
                      <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                        {group.label}
                      </p>
                      <p className="text-xs text-gray-400">{group.description}</p>
                    </div>
                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-800">
                      {group.modules.map((module) => {
                        // Skip dotted child keys at the top level — they are
                        // rendered INSIDE their parent row below. This is now
                        // generic (not training_programs-specific), so any
                        // module that registers action/sub-permissions (e.g.
                        // trial_reports.export, future attendance.finalize)
                        // renders its children automatically.
                        if (module.includes('.')) return null;
                        const level = permissions[module] || 'none';
                        const defaultLevel = detail?.role_defaults?.[module] || 'none';
                        const isCustom = !useRoleDefault && level !== defaultLevel;
                        const children = group.modules.filter(m => m.startsWith(module + '.'));
                        const hasChildren = children.length > 0;
                        return (
                          <div key={module} data-testid={`access-row-${module}`}>
                            <div className="flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-900">
                              <div className="min-w-0 mr-3">
                                <p className="text-sm font-medium truncate">{moduleLabels[module] || module}</p>
                                {isCustom && (
                                  <span className="text-[10px] text-blue-600 uppercase tracking-wide">Customized</span>
                                )}
                                {hasChildren && (
                                  <p className="text-[10px] text-gray-400 mt-0.5">
                                    Use the action / sub-permissions below to control specific actions for this module.
                                  </p>
                                )}
                              </div>
                              <PermissionSegmented
                                value={level}
                                onChange={(next) => {
                                  handlePermissionChange(module, next);
                                  // If parent is demoted to none, cascade children to none.
                                  if (hasChildren && next === 'none') {
                                    children.forEach(c => handlePermissionChange(c, 'none'));
                                  }
                                }}
                                disabled={useRoleDefault}
                              />
                            </div>
                            {hasChildren && level !== 'none' && (
                              <div className="bg-gray-50 dark:bg-gray-900/40 border-t border-gray-100 dark:border-gray-800" data-testid={module === 'training_programs' ? 'training-programs-children' : `${module}-children`}>
                                {children.map(childKey => {
                                  const childLevel = permissions[childKey] || 'none';
                                  const childDefault = detail?.role_defaults?.[childKey] || 'none';
                                  const childCustom = !useRoleDefault && childLevel !== childDefault;
                                  return (
                                    <div
                                      key={childKey}
                                      className="flex items-center justify-between pl-8 pr-3 py-2 hover:bg-gray-100/60 dark:hover:bg-gray-900"
                                      data-testid={`access-row-${childKey}`}
                                    >
                                      <div className="min-w-0 mr-3 flex items-center gap-2">
                                        <span className="text-gray-300 dark:text-gray-600 text-xs">└─</span>
                                        <p className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
                                          {moduleLabels[childKey] || childKey}
                                        </p>
                                        {childCustom && (
                                          <span className="text-[10px] text-blue-600 uppercase tracking-wide">Customized</span>
                                        )}
                                      </div>
                                      <PermissionSegmented
                                        value={childLevel}
                                        onChange={(next) => handlePermissionChange(childKey, next)}
                                        disabled={useRoleDefault}
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Athlete Access (Staff only) */}
            {isStaff && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-medium">Athlete Access</Label>
                  <span className="text-xs text-gray-500" data-testid="athletes-selected-count">
                    {athleteIds.length} of {allAthletes.length} selected
                  </span>
                </div>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <Button variant="outline" size="sm" onClick={selectAllAthletes} data-testid="athletes-select-all">
                    Select All
                  </Button>
                  <Button variant="outline" size="sm" onClick={clearAllAthletes} data-testid="athletes-clear-all">
                    Clear All
                  </Button>
                  <Button variant="outline" size="sm" onClick={matchRoleDefaultAthletes} data-testid="athletes-match-default">
                    Match Role Default
                  </Button>
                </div>
                <Input
                  placeholder="Search athletes..."
                  value={athleteSearch}
                  onChange={(e) => setAthleteSearch(e.target.value)}
                  className="mb-2"
                  data-testid="athlete-search-input"
                />
                <div className="border rounded-lg max-h-56 overflow-y-auto">
                  {filteredAthletes.length === 0 ? (
                    <p className="text-sm text-gray-400 p-3">No athletes found.</p>
                  ) : (
                    filteredAthletes.map((a) => (
                      <label
                        key={a.id}
                        className="flex items-center gap-2 p-2 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                        data-testid={`athlete-option-${a.id}`}
                      >
                        <Checkbox
                          checked={athleteIds.includes(a.id)}
                          onCheckedChange={() => toggleAthlete(a.id)}
                        />
                        <span className="text-sm flex-1 truncate">{a.full_name}</span>
                        {a.jersey_number != null && (
                          <span className="text-xs text-gray-500">#{a.jersey_number}</span>
                        )}
                      </label>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Impact warning */}
            <div className="flex gap-2 items-start p-3 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
              <span className="text-amber-600 text-lg leading-none">⚠️</span>
              <p className="text-xs text-amber-800 dark:text-amber-200">
                Saving changes will immediately update access and <strong>log the user out of active sessions</strong>.
              </p>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between pt-4 border-t gap-2 flex-wrap">
              <Button
                variant="ghost"
                className="text-gray-600"
                onClick={handleResetDefault}
                disabled={saving || !detail.has_overrides}
                data-testid="reset-to-default-btn"
              >
                Reset to Role Default
              </Button>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="cancel-edit-btn">
                  Cancel
                </Button>
                <Button
                  className="bg-pfap-green hover:bg-pfap-green/90"
                  onClick={handleSave}
                  disabled={saving}
                  data-testid="save-access-btn"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default EditAccessSheet;
